import type {
  WorkPackage,
  Worksite,
  ComplianceEvaluation,
  ComplianceLevel,
  TradeCategory,
  EquipmentCondition,
  FilterTab,
} from '../components/capex/types'

// ---------------------------------------------------------------------------
// Cost Computation
// ---------------------------------------------------------------------------

/**
 * Display cost for a single work package.
 * Returns the quote amount if available, otherwise the average of low/high estimates.
 */
export function packageCost(pkg: WorkPackage): number {
  return pkg.quote_amount ?? ((pkg.cost_low ?? 0) + (pkg.cost_high ?? 0)) / 2
}

/**
 * Low-end total cost for a worksite across all its work packages.
 * Uses quote_amount when available, otherwise cost_low.
 */
export function worksiteCostLow(ws: Worksite): number {
  return (ws.work_packages ?? []).reduce(
    (sum, pkg) => sum + (pkg.quote_amount ?? (pkg.cost_low ?? 0)),
    0
  )
}

/**
 * High-end total cost for a worksite across all its work packages.
 * Uses quote_amount when available, otherwise cost_high.
 */
export function worksiteCostHigh(ws: Worksite): number {
  return (ws.work_packages ?? []).reduce(
    (sum, pkg) => sum + (pkg.quote_amount ?? (pkg.cost_high ?? 0)),
    0
  )
}

/**
 * Human-readable cost display for a worksite.
 * e.g. "45k€" or "28–36k€"
 */
export function worksiteCostDisplay(ws: Worksite, currency?: string | null): string {
  const low = worksiteCostLow(ws)
  const high = worksiteCostHigh(ws)
  return low === high
    ? formatCost(low, currency)
    : `${formatCostRaw(low)}–${formatCost(high, currency)}`
}

/**
 * Format a cost amount as a compact string (e.g. "45k €" for ≥1000, "800 €"
 * otherwise), with the currency of the plan's jurisdiction.
 *
 * The euro used to be baked into this string, so a works plan for a building in
 * London printed its budget in euros. The currency now comes from the plan
 * (`capex_plans.currency`, resolved from the asset's country); when it is
 * unknown the figure is rendered bare, because a wrong symbol misleads and a
 * missing one merely under-informs.
 */
export function formatCost(amount: number, currency?: string | null): string {
  const symbol = currencySymbol(currency)
  return amount >= 1000
    ? `${Math.round(amount / 1000)}k${symbol}`
    : `${Math.round(amount)}${symbol}`
}

/**
 * The currency's symbol, prefixed by a space, or an empty string when unknown.
 * Uses the runtime's own table rather than a hand-maintained map, so a currency
 * we never anticipated still renders correctly.
 */
function currencySymbol(currency?: string | null): string {
  if (!currency) return ''
  try {
    const parts = new Intl.NumberFormat(undefined, { style: 'currency', currency })
      .formatToParts(0)
    const found = parts.find((p) => p.type === 'currency')
    return found ? ` ${found.value}` : ` ${currency}`
  } catch {
    return ` ${currency}`
  }
}

/**
 * Format a cost amount without trailing "€" (used for range display).
 */
function formatCostRaw(amount: number): string {
  return amount >= 1000
    ? `${Math.round(amount / 1000)}k`
    : `${Math.round(amount)}`
}

/**
 * Total annual budget for a given year across all non-ignored worksites.
 * Uses the average of low/high for each worksite.
 */
export function yearBudget(worksites: Worksite[], year: number): number {
  return worksites
    .filter(ws => ws.year === year && ws.status !== 'ignored')
    .reduce((sum, ws) => sum + (worksiteCostLow(ws) + worksiteCostHigh(ws)) / 2, 0)
}

// ---------------------------------------------------------------------------
// Compliance Level Computation
// ---------------------------------------------------------------------------

const COMPLIANCE_ORDER: ComplianceLevel[] = ['breach', 'overdue', 'tight', 'ok']

/**
 * Compute the compliance level for a single work package.
 * Returns null when the package has no linked evaluation.
 */
export function complianceLevel(
  _pkg: WorkPackage,
  worksiteYear: number,
  evaluation: ComplianceEvaluation | null
): ComplianceLevel | null {
  if (!evaluation) return null

  const currentYear = new Date().getFullYear()

  // A NON_COMPLIANT evaluation with no next_evaluation_at is an active breach
  if (evaluation.status === 'NON_COMPLIANT' && !evaluation.next_evaluation_at) {
    return 'breach'
  }

  const deadlineYear = evaluation.next_evaluation_at
    ? new Date(evaluation.next_evaluation_at).getFullYear()
    : null

  if (!deadlineYear) return null

  if (deadlineYear < currentYear) return 'breach'   // Already in violation
  if (deadlineYear < worksiteYear) return 'overdue'  // Planned too late
  if (deadlineYear === worksiteYear) return 'tight'  // Deadline year
  return 'ok'
}

/**
 * Compute the worst compliance level across all work packages of a worksite.
 * Returns null when no packages have a linked evaluation.
 */
export function worstWorksiteCompliance(
  ws: Worksite,
  evaluations: Map<string, ComplianceEvaluation>
): ComplianceLevel | null {
  return (ws.work_packages ?? []).reduce<ComplianceLevel | null>((worst, pkg) => {
    const evaluation = pkg.compliance_evaluation_uid
      ? evaluations.get(pkg.compliance_evaluation_uid) ?? null
      : null
    const level = complianceLevel(pkg, ws.year ?? 0, evaluation)
    if (!level) return worst
    if (!worst) return level
    return COMPLIANCE_ORDER.indexOf(level) < COMPLIANCE_ORDER.indexOf(worst) ? level : worst
  }, null)
}

// ---------------------------------------------------------------------------
// Filter Logic
// ---------------------------------------------------------------------------

/**
 * Filter worksites based on the active tab.
 * - 'all'        → excludes 'ignored'
 * - 'compliance' → worksites where worst compliance is 'breach' or 'overdue'
 * - 'pending'    → status in ['draft', 'reviewed']
 * - 'validated'  → status === 'validated'
 * - 'deferred'   → status === 'deferred'
 *
 * The compliance tab requires evaluations to be available via a closure or
 * pre-computed per-worksite compliance. For the basic filter (without evaluations),
 * compliance tab defaults to showing all non-ignored worksites.
 */
export function filterWorksites(worksites: Worksite[], tab: FilterTab): Worksite[] {
  switch (tab) {
    case 'all':
      return worksites.filter(ws => ws.status !== 'ignored')
    case 'pending':
      return worksites.filter(ws => ws.status === 'draft' || ws.status === 'reviewed')
    case 'validated':
      return worksites.filter(ws => ws.status === 'validated')
    case 'deferred':
      return worksites.filter(ws => ws.status === 'deferred')
    case 'compliance':
      // Caller must pre-filter with evaluation data; this fallback excludes ignored
      return worksites.filter(ws => ws.status !== 'ignored')
    default:
      return worksites.filter(ws => ws.status !== 'ignored')
  }
}

/**
 * Filter worksites for the compliance tab, requiring evaluations map.
 */
export function filterWorksitesByCompliance(
  worksites: Worksite[],
  evaluations: Map<string, ComplianceEvaluation>
): Worksite[] {
  return worksites.filter(ws => {
    const level = worstWorksiteCompliance(ws, evaluations)
    return level === 'breach' || level === 'overdue'
  })
}

// ---------------------------------------------------------------------------
// Smoothing Algorithm
// ---------------------------------------------------------------------------

/**
 * Automatically smooth the plan budget by distributing excess worksites into
 * future years. Protected worksites (validated or with breach/overdue compliance)
 * are never moved.
 *
 * @param worksites   Flat list of worksites (will be cloned — originals unmodified)
 * @param horizonStart First year of the plan horizon
 * @param horizonEnd   Last year of the plan horizon
 * @param complianceLevelFn  Function to get the worst compliance level for a worksite
 * @returns { result, protectedCount } where result is the updated worksite list
 */
export function smoothPlan(
  worksites: Worksite[],
  horizonStart: number,
  horizonEnd: number,
  complianceLevelFn: (ws: Worksite) => ComplianceLevel | null
): { result: Worksite[]; protectedCount: number } {
  const result: Worksite[] = worksites.map(ws => ({ ...ws }))

  const years: number[] = []
  for (let y = horizonStart; y <= horizonEnd; y++) {
    years.push(y)
  }

  // Protected worksites are never moved by the algorithm
  const isProtected = (ws: Worksite): boolean => {
    if (ws.status === 'validated') return true
    const level = complianceLevelFn(ws)
    return level === 'breach' || level === 'overdue'
  }

  // Compute target: total budget / number of years
  const totalBudget = result
    .filter(ws => ws.status !== 'ignored')
    .reduce((sum, ws) => sum + (worksiteCostLow(ws) + worksiteCostHigh(ws)) / 2, 0)
  const targetPerYear = years.length > 0 ? totalBudget / years.length : 0

  let protectedCount = 0

  for (const year of years) {
    const yearWorksites = result.filter(ws => ws.year === year && ws.status !== 'ignored')
    const yearBudgetVal = yearWorksites.reduce(
      (sum, ws) => sum + (worksiteCostLow(ws) + worksiteCostHigh(ws)) / 2,
      0
    )

    if (yearBudgetVal <= targetPerYear) continue

    // Sort movable worksites by cost ascending (move cheapest first)
    const movableInYear = yearWorksites
      .filter(ws => !isProtected(ws) && ws.status === 'draft')
      .sort(
        (a, b) =>
          (worksiteCostLow(a) + worksiteCostHigh(a)) / 2 -
          (worksiteCostLow(b) + worksiteCostHigh(b)) / 2
      )

    let excess = yearBudgetVal - targetPerYear

    for (const ws of movableInYear) {
      if (excess <= 0) break
      const cost = (worksiteCostLow(ws) + worksiteCostHigh(ws)) / 2
      const targetYear = findNextYearWithCapacity(result, year, horizonEnd, targetPerYear)
      if (!targetYear) break // No room left in horizon
      ws.year = targetYear
      ws.status = 'deferred'
      ws.deferred_from_year = ws.deferred_from_year ?? year
      excess -= cost
    }

    protectedCount += yearWorksites.filter(isProtected).length
  }

  return { result, protectedCount }
}

/**
 * Find the nearest future year whose current budget is below the target.
 */
function findNextYearWithCapacity(
  worksites: Worksite[],
  afterYear: number,
  horizonEnd: number,
  targetPerYear: number
): number | null {
  for (let y = afterYear + 1; y <= horizonEnd; y++) {
    const budget = worksites
      .filter(ws => ws.year === y && ws.status !== 'ignored')
      .reduce((sum, ws) => sum + (worksiteCostLow(ws) + worksiteCostHigh(ws)) / 2, 0)
    if (budget < targetPerYear) return y
  }
  return null
}

// ---------------------------------------------------------------------------
// Trade Metadata
// ---------------------------------------------------------------------------

export const TRADE_META: Record<
  TradeCategory,
  {
    label: string   // i18n key
    color: string   // Tailwind border/accent color class
    order: number   // Display sort order
  }
> = {
  structural: {
    label: 'capex.trade.structural',
    color: 'border-stone-500',
    order: 1,
  },
  envelope: {
    label: 'capex.trade.envelope',
    color: 'border-orange-500',
    order: 2,
  },
  hvac: {
    label: 'capex.trade.hvac',
    color: 'border-blue-500',
    order: 3,
  },
  electrical: {
    label: 'capex.trade.electrical',
    color: 'border-yellow-500',
    order: 4,
  },
  fire_safety: {
    label: 'capex.trade.fire_safety',
    color: 'border-red-500',
    order: 5,
  },
  vertical_transport: {
    label: 'capex.trade.vertical_transport',
    color: 'border-purple-500',
    order: 6,
  },
  plumbing: {
    label: 'capex.trade.plumbing',
    color: 'border-cyan-500',
    order: 7,
  },
}

// ---------------------------------------------------------------------------
// Condition Metadata
// ---------------------------------------------------------------------------

export const CONDITION_META: Record<
  EquipmentCondition,
  {
    label: string  // "New", "Good", "Fair", "Poor", "End of life"
    color: string  // Badge color class
  }
> = {
  1: { label: 'New', color: 'bg-green-100 text-green-800' },
  2: { label: 'Good', color: 'bg-lime-100 text-lime-800' },
  3: { label: 'Fair', color: 'bg-yellow-100 text-yellow-800' },
  4: { label: 'Poor', color: 'bg-orange-100 text-orange-800' },
  5: { label: 'End of life', color: 'bg-red-100 text-red-800' },
}

// ---------------------------------------------------------------------------
// Compliance Colors
// ---------------------------------------------------------------------------

export const COMPLIANCE_COLORS: Record<
  ComplianceLevel,
  {
    border: string
    bg: string
    text: string
    badge: string
  }
> = {
  breach: {
    border: 'border-red-500',
    bg: 'bg-red-50',
    text: 'text-red-700',
    badge: 'bg-red-100 text-red-800',
  },
  overdue: {
    border: 'border-orange-500',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    badge: 'bg-orange-100 text-orange-800',
  },
  tight: {
    border: 'border-yellow-500',
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    badge: 'bg-yellow-100 text-yellow-800',
  },
  ok: {
    border: 'border-green-500',
    bg: 'bg-green-50',
    text: 'text-green-700',
    badge: 'bg-green-100 text-green-800',
  },
}
